/**
 * One-time cleanup for accessibility state left over from earlier prototypes.
 *
 * `@delasoft/accessibility` restores saved settings (font scale, color mode,
 * spacing…) from `localStorage['delasoft.a11y.settings.<theme.id>']` on its
 * very first mount — deliberately, so a returning visitor's choices are live
 * before they open the panel (see `SettingsContext.tsx` in that package).
 *
 * Two earlier integration attempts in this app used the package's default
 * theme id, `west-virginia` — a standalone `<script>` embed, and an earlier
 * revision of this React integration. Anything toggled while testing either
 * one is still sitting under that key and would get replayed here even
 * though the current theme (`portalAccessibilityTheme`, id `wv-onestop-portal`)
 * never wrote it. Call this once, before the widget mounts, so a fresh load
 * of this app can never inherit it.
 *
 * Safe to delete once enough time has passed that no browser could still be
 * carrying the old key (or immediately, if you don't care about that edge).
 */
const SETTINGS_KEY_PREFIX = 'delasoft.a11y.settings.';
const CURRENT_THEME_ID = 'wv-onestop-portal';
const EFFECT_STYLE_ID = 'delasoft-a11y-effects';
const ATTR_PREFIX = 'data-dela11y-';
export function clearLegacyAccessibilityState() {
    if (typeof window === 'undefined')
        return;
    try {
        const stale = [];
        for (let i = 0; i < window.localStorage.length; i += 1) {
            const key = window.localStorage.key(i);
            if (key && key.startsWith(SETTINGS_KEY_PREFIX) && key !== SETTINGS_KEY_PREFIX + CURRENT_THEME_ID) {
                stale.push(key);
            }
        }
        stale.forEach((key) => window.localStorage.removeItem(key));
    }
    catch {
        /* localStorage unavailable (private browsing, locked-down policy) — nothing to clear. */
    }
    if (typeof document === 'undefined')
        return;
    /* Belt-and-suspenders: a same-session leftover (e.g. hot reload) could still have these live. */
    document.getElementById(EFFECT_STYLE_ID)?.remove();
    const root = document.documentElement;
    root.getAttributeNames()
        .filter((name) => name.startsWith(ATTR_PREFIX))
        .forEach((name) => root.removeAttribute(name));
}
